import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentService } from '@blk/cwp-ng-framework';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { AudienceService, ComponentData } from '@ddt-web/data-service';
import { ResponsiveDisplayConstants } from '@ddt-web/dom';
import { Memoize, SlugifyPipe } from '@ddt-web/generic-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { SidenavRoutePath } from './sidenav-route-path.interface';

@UntilDestroy()
@Component({
	selector: 'ac-nav-sidenav',
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnInit {
	@InjectConfig(SharedConfigKey.SIDENAV_ROUTE_PATHS) private readonly configLayerRoutePaths: SidenavRoutePath[] = [];

	@Input() public customShowRules: { [id: string]: boolean };
	@Input() public expandOnFocus: boolean = false;
	@Input() public data: ComponentData;
	@Input() public enableGutter: boolean = true;

	public routePathsToShow: SidenavRoutePath[];
	public currentPathIndex: number;
	public queryParams: any;
	public expanded = false;
	public navItems: NavItems[] = [];

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private contentService: ContentService,
		private audienceService: AudienceService
	) { }

	public ngOnInit(): void {
		this.initialiseRoutePathsToShow();
		this.updateCurrentPathIndex();
		this.route.queryParams.pipe(untilDestroyed(this)).subscribe(params => {
			this.queryParams = params;
		});
		this.router.events.pipe(untilDestroyed(this)).subscribe(_ => this.updateCurrentPathIndex());
	}

	private initialiseRoutePathsToShow(): void {
		this.routePathsToShow = this.configLayerRoutePaths?.filter(path => path.show && (!this.customShowRules?.hasOwnProperty(path?.id) || this.customShowRules[path.id]));

		if (this.routePathsToShow.every(routePath => routePath.order)) {
			this.routePathsToShow.sort((routePath1, routePath2) => {
				return routePath1.order - routePath2.order;
			});
		}
		this.filterRoutePaths();
	}

	private filterRoutePaths(): void {
		this.audienceService.getViewCategory().pipe(untilDestroyed(this)).subscribe(viewCat => {
			this.routePathsToShow.filter(item => !item.showOnlyInView || (item.showOnlyInView && item.showOnlyInView.includes(viewCat)))
				.forEach(item => this.navItems.push({ route: item.route, showNewFlag: item.showNewFlag }));
		});
	}

	public toggleMenu(): void {
		if (window.innerWidth <= ResponsiveDisplayConstants.DEVICE_WIDTH.MOBILE_MAX) {
			this.expanded = !this.expanded;
		} else {
			this.expanded = false;
		}
	}

	@Memoize()
	public shouldShowLeftArrow(currentPathIndex: number): boolean {
		return currentPathIndex > 0;
	}

	@Memoize()
	public shouldShowRightArrow(currentPathIndex: number): boolean {
		return currentPathIndex < this.routePathsToShow.length - 1;
	}

	private updateCurrentPathIndex(): void {
		const currentRouteFromUrl: string = this.router.url;
		const separators = ['/', '\\\?'];
		const urlElements = currentRouteFromUrl.split(new RegExp(separators.join('|'), 'g'));

		this.currentPathIndex = this.routePathsToShow.findIndex(path => {
			if (typeof path.route === 'string') {
				return currentRouteFromUrl.startsWith(path.route) || currentRouteFromUrl.startsWith('/analysis/' + path.route); // needed for nile where path does not include /analysis

			} else if (this.isChildRoute(path)) {
				// assumes that the parent route and named outlet do not have to be adjacent in the path
				return (urlElements.includes(path.parentRoute) &&
					this.getNamedOutletsFromUrl(currentRouteFromUrl)[0] === this.createNamedOutletUrlFromRoutePath(path));
			}
		});
	}

	private isChildRoute(routePath: SidenavRoutePath): boolean {
		return !!routePath.parentRoute;
	}

	private createNamedOutletUrlFromRoutePath(routePath: SidenavRoutePath): string {
		if (typeof routePath.route !== 'string') {
			const outletPaths = routePath.route.outlets;
			const outletUrl: string[] = [];

			Object.keys(outletPaths).forEach((outletName, idx, keys) => {
				if (idx === 0) {
					outletUrl.push('(');
				}
				outletUrl.push(`${outletName}:${outletPaths[outletName][0]}`);
				if (idx !== keys.length - 1) {
					outletUrl.push('//');
				} else {
					outletUrl.push(')');
				}
			});

			return outletUrl.join('');
		}
	}

	@Memoize()
	public getPreviousRoute(currentPathIndex: number): string | { outlets: { [outletName: string]: string[] } } {
		const previousRoute = this.routePathsToShow[currentPathIndex - 1];
		return previousRoute.route;
	}

	@Memoize()
	public getNextRoute(currentPathIndex: number): string | { outlets: { [outletName: string]: string[] } } {
		const nextRoute = this.routePathsToShow[currentPathIndex + 1];
		return nextRoute.route;
	}

	// This is only for mobile view
	@Memoize()
	public getPreviousLabel(currentPathIndex: number): string {
		const previousRoute = this.routePathsToShow[currentPathIndex - 1];
		const labelDcrFromId = this.getLabelDcrFromId(previousRoute.id);
		return this.contentService.get(labelDcrFromId, labelDcrFromId);
	}

	// This is only for mobile view
	@Memoize()
	public getNextLabel(currentPathIndex: number): string {
		const nextRoute = this.routePathsToShow[currentPathIndex + 1];
		const labelDcrFromId = this.getLabelDcrFromId(nextRoute.id);
		return this.contentService.get(labelDcrFromId, labelDcrFromId);
	}

	private getNamedOutletsFromUrl(url: string): string[] {
		const regExp = /\(([^)]+)\)/;
		return regExp.exec(url);
	}

	private getLabelDcrFromId(id: string) {
		return `${new SlugifyPipe().transform(id, '_')?.toUpperCase()}_TAB_NAME`;
	}
}

export interface NavItems {
	route: string | { outlets: { [outletName: string]: string[] } };
	showNewFlag?: boolean;
}
